import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, Loader2 } from 'lucide-react';
import {
  creativeStrategyApi,
  type CreativeStrategyItem,
  type StrategyDocketDetail,
  type StrategyDocketItems,
  type StrategyDocketUsage,
} from '@/services/api';
import { DocketItemDrawer } from '@/components/knowledge/creative-strategy/docket/DocketItemDrawer';
import { DocketExemplarsTab } from '@/components/knowledge/creative-strategy/docket/DocketExemplarsTab';
import { DocketUsageTab } from '@/components/knowledge/creative-strategy/docket/Usage';
import { DocketVersionsTab } from '@/components/knowledge/creative-strategy/docket/DocketVersionsTab';
import { DocketRunsTab } from '@/components/knowledge/creative-strategy/docket/DocketRunsTab';
import { DocketExploreTab } from '@/components/knowledge/creative-strategy/docket/DocketExploreTab';
import { DocketProposalsTab } from '@/components/knowledge/creative-strategy/docket/DocketProposalsTab';
import { DocketFindingsTab } from '@/components/knowledge/creative-strategy/docket/DocketFindingsTab';

type ItemTab = 'concepts' | 'strategies' | 'templates';
type Tab = ItemTab | 'explore' | 'findings' | 'proposals' | 'exemplars' | 'usage' | 'sources' | 'runs' | 'versions';

const TABS: Tab[] = ['strategies', 'templates', 'concepts', 'explore', 'findings', 'proposals', 'exemplars', 'usage', 'sources', 'runs', 'versions'];

const CLASS_OF: Record<ItemTab, string> = {
  concepts: 'market_concept',
  strategies: 'creative_strategy',
  templates: 'creative_pattern',
};

function isItemTab(t: Tab): t is ItemTab {
  return t in CLASS_OF;
}

function parseTab(raw: string | null): Tab {
  return (TABS as string[]).includes(raw || '') ? (raw as Tab) : 'strategies';
}

export function DocketPage() {
  const { docketId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const openItemId = searchParams.get('item');
  const [detail, setDetail] = useState<StrategyDocketDetail | null>(null);
  const [items, setItems] = useState<StrategyDocketItems | null>(null);
  const [member, setMember] = useState<string>('');
  const [usage, setUsage] = useState<StrategyDocketUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const load = useCallback(async (clientUid?: string) => {
    setError(null);
    const [d, rows] = await Promise.all([
      creativeStrategyApi.getDocket(docketId),
      creativeStrategyApi.docketItems(docketId, clientUid),
    ]);
    setDetail(d);
    setItems(rows);
    setMember(rows.client_uid || '');
  }, [docketId]);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  const setTab = useCallback(
    (t: Tab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', t);
        return next;
      });
    },
    [setSearchParams],
  );

  const openItem = useCallback(
    (id: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('item', id);
        else next.delete('item');
        return next;
      });
    },
    [setSearchParams],
  );
  const closeItem = useCallback(() => openItem(null), [openItem]);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      setUsage(await creativeStrategyApi.docketUsage(docketId));
    } catch (err) {
      setUsageError((err as Error).message);
    } finally {
      setUsageLoading(false);
    }
  }, [docketId]);

  // Usage scans every member's briefs: load it once, when something needs it.
  const needsUsage = tab === 'usage' || !!openItemId;
  useEffect(() => {
    if (needsUsage && !usage && !usageLoading && !usageError) loadUsage();
  }, [needsUsage, usage, usageLoading, usageError, loadUsage]);

  const classItems: CreativeStrategyItem[] = isItemTab(tab)
    ? (items?.items || []).filter((it) => it.entity_class === CLASS_OF[tab])
    : [];
  const scopes = Array.from(new Set(classItems.map((it) => it.scope))).sort();
  const shown = tab === 'templates' && scopeFilter !== 'all'
    ? classItems.filter((it) => it.scope === scopeFilter)
    : classItems;

  async function rebuild(dryRun: boolean, promote = false) {
    setBusy(true);
    setError(null);
    try {
      const res = await creativeStrategyApi.rebuildDocket(docketId, dryRun, promote);
      setRunId(res.run_id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleHidden(item: CreativeStrategyItem) {
    await creativeStrategyApi.setDocketItem(docketId, item.resolved_id, { hidden: !item.hidden });
    await load(member);
  }

  const setItem = useCallback(
    async (itemId: string, body: { hidden: boolean; note?: string | null }) => {
      await creativeStrategyApi.setDocketItem(docketId, itemId, body);
      await load(member);
    },
    [docketId, load, member],
  );

  if (!detail) {
    return (
      <div className="p-6 text-sm text-slate-500">
        {error ? <span className="text-red-700">{error}</span> : <><Loader2 size={14} className="inline animate-spin mr-2" />Loading library…</>}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <Link to="/knowledge/creative-strategy/library" className="text-xs text-slate-500 hover:text-navy-900 inline-flex items-center gap-1 mb-2">
          <ArrowLeft size={12} /> Library
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-navy-900">{detail.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5 font-mono">{detail.scope} · served {detail.served_version ?? 'none'}</p>
          </div>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => rebuild(true)} className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white">
              Dry run
            </button>
            <button type="button" disabled={busy} onClick={() => rebuild(false)} className="text-sm px-3 py-1.5 rounded-lg bg-navy-900 text-white">
              {busy ? 'Starting…' : 'Rebuild'}
            </button>
            <button type="button" disabled={busy} onClick={() => rebuild(false, true)} className="text-sm px-3 py-1.5 rounded-lg border border-navy-900 text-navy-900 bg-white">
              Promote
            </button>
          </div>
        </div>
        {runId && <p className="mt-2 text-xs text-slate-600">Garden run <span className="font-mono">{runId}</span> started. Watch it in Garden Studio.</p>}
        {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
      </div>

      <div className="px-6 py-3 bg-white border-b border-slate-200 flex items-center gap-3">
        <label className="text-xs text-slate-500">Overlay</label>
        <select
          className="text-sm border border-slate-300 rounded-lg px-2 py-1"
          value={member}
          onChange={(e) => {
            const next = e.target.value;
            setMember(next);
            load(next).catch((err: Error) => setError(err.message));
          }}
        >
          {detail.member_clients.map((uid) => (
            <option key={uid} value={uid}>{uid}</option>
          ))}
        </select>
        <span className="text-xs text-slate-400">{items?.overlay_method || 'no overlay'}</span>
        {!!items?.gaps.length && <span className="text-xs text-amber-700">{items.gaps.length} gaps</span>}
      </div>

      <div className="px-6 pt-3 flex gap-1 items-end">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'text-xs px-3 py-1.5 rounded-t-lg border border-b-0',
              tab === t ? 'bg-white border-slate-200 text-navy-900' : 'border-transparent text-slate-500',
            )}
          >
            {t}
          </button>
        ))}
        {tab === 'templates' && (
          <select
            className="ml-auto text-xs border border-slate-300 rounded-lg px-2 py-1 mb-1"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            aria-label="Template scope"
          >
            <option value="all">All scopes</option>
            {scopes.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="bg-white border border-slate-200 rounded-b-xl rounded-tr-xl p-4">
          {tab === 'sources' && (
            <dl className="text-sm space-y-2">
              <div><dt className="text-xs text-slate-500">Shopify category</dt><dd>{detail.shopify_category_path.join(' > ') || detail.shopify_category_id || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Amazon paths</dt><dd>{detail.amazon_paths.join(' · ') || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Competitors</dt><dd>{detail.competitor_set.join(', ') || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Product kind</dt><dd>{detail.product_kind}</dd></div>
            </dl>
          )}
          {tab === 'runs' && <DocketRunsTab docketId={docketId} />}
          {tab === 'explore' && <DocketExploreTab docketId={docketId} onOpenItem={openItem} />}
          {tab === 'findings' && <DocketFindingsTab docketId={docketId} />}
          {tab === 'proposals' && (
            <DocketProposalsTab docketId={docketId} clientUid={member || null} onOpenItem={openItem} />
          )}
          {tab === 'versions' && <DocketVersionsTab docketId={docketId} detail={detail} />}
          {tab === 'exemplars' && (
            <DocketExemplarsTab docketId={docketId} version={items?.version ?? detail.served_version} onOpenItem={openItem} />
          )}
          {tab === 'usage' && (
            <DocketUsageTab usage={usage} loading={usageLoading} error={usageError} onOpenItem={openItem} onReload={loadUsage} />
          )}
          {isItemTab(tab) && (
            <ul className="divide-y divide-slate-100">
              {shown.map((it) => {
                const used = usage?.items[it.resolved_id];
                const exemplars = it.exemplar_ad_keys?.length ?? 0;
                return (
                  <li
                    key={it.resolved_id}
                    className={clsx(
                      'py-2 px-2 -mx-2 rounded-lg flex items-start justify-between gap-4 cursor-pointer hover:bg-slate-50',
                      openItemId === it.resolved_id && 'bg-teal-50/50',
                    )}
                    onClick={() => openItem(it.resolved_id)}
                  >
                    <div className="min-w-0">
                      <button type="button" className={clsx('font-medium text-left hover:text-brand-teal-dark', it.hidden && 'line-through text-slate-400')}>
                        {it.canonical_name}
                      </button>
                      <div className="text-xs text-slate-500 font-mono">{it.resolved_id}</div>
                      {it.in_client_graph != null && (
                        <div className="text-xs text-slate-500">in client graph: {String(it.in_client_graph)}{it.graph_concept ? ` · ${it.graph_concept}` : ''}</div>
                      )}
                      {it.client_runs_it != null && <div className="text-xs text-slate-500">client runs it: {String(it.client_runs_it)}</div>}
                    </div>
                    <div className="flex items-start gap-4 flex-shrink-0">
                      <div className="text-right text-[11px] text-slate-500 tabular-nums leading-snug">
                        {it.entity_class !== 'market_concept' ? (
                          <>
                            {it.advertisers != null && <div>{it.advertisers.toLocaleString()} advertisers</div>}
                            {it.support != null && <div>{it.support.toLocaleString()} ads</div>}
                            {exemplars > 0 && <div className="text-slate-400">{exemplars} exemplars</div>}
                          </>
                        ) : (
                          it.support != null && <div>{it.support.toLocaleString()} mentions</div>
                        )}
                        {used && (used.cited > 0 || used.handoffs > 0) && (
                          <div className="text-emerald-700">
                            {used.cited} cited{used.handoffs ? ` · ${used.handoffs} run` : ''}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="text-xs text-slate-600 underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHidden(it);
                        }}
                      >
                        {it.hidden ? 'Unhide' : 'Hide'}
                      </button>
                    </div>
                  </li>
                );
              })}
              {shown.length === 0 && <li className="text-sm text-slate-500">Nothing in this tab.</li>}
            </ul>
          )}
        </div>
      </div>

      <DocketItemDrawer
        docketId={docketId}
        itemId={openItemId}
        version={items?.version}
        clientUid={member || null}
        usage={usage}
        usageLoading={usageLoading}
        onOpenItem={openItem}
        onClose={closeItem}
        onSetItem={setItem}
      />
    </div>
  );
}
